import { Box, Typography } from "@mui/material";
import { density } from "@app/design-tokens";
import { Link, useLocation } from "react-router";

import { useAuth } from "@/auth/authContext";

import { activeSection, NAV_SECTIONS } from "./navSections";

/**
 * 手機版的底部導覽列。
 *
 * 跟桌機的 rail 讀同一份 `NAV_SECTIONS`，所以兩邊的項目、順序與目前位置的
 * 判斷不可能不一致 —— 一份宣告、兩種排版。
 *
 * 樣式改成貼底的系統列（參考 Airbnb 手機版），不是浮在內容上的卡片：
 * 貼齊螢幕左右與底部、方角、只用一條 1px 的上邊框跟內容分開，不用陰影、
 * 不用圓角——這樣它讀起來是畫面本身的一部分，不是蓋在畫面上的元件。
 *
 * 幾個仍然保留的決定：
 *
 * - **圖示 44px 的點擊區**：低於這個尺寸拇指會點不準（Apple 建議 44pt、
 *   Material 建議 48dp）。文字標籤只是輔助，它本身不決定點擊區大小。
 * - **文字標籤留著**：純圖示在手機上辨識度不足，而底部列有寬度可以放字。
 * - **`env(safe-area-inset-bottom)`**：貼底之後底部列真的頂到螢幕邊緣，
 *   iOS 的 home indicator 需要這段安全區間距，否則最下面一排的點擊區會被
 *   系統手勢吃掉——這是貼底設計換來的成本，浮動設計靠留白天然閃過了它。
 */
export function MobileTabBar() {
  const { me } = useAuth();
  const location = useLocation();
  const serviceCodes = (me?.services ?? []).map((service) => service.code);
  const current = activeSection(location.pathname, serviceCodes);

  return (
    <Box
      component="nav"
      aria-label="主導覽"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar,
        height: `calc(${density.tabBarHeight}px + env(safe-area-inset-bottom, 0px))`,
        pb: "env(safe-area-inset-bottom, 0px)",
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        display: "flex",
        alignItems: "stretch",
      }}
    >
      {NAV_SECTIONS.map((section) => {
        const active = current?.key === section.key;
        const Icon = active ? section.activeIcon : section.icon;

        return (
          <Box
            key={section.key}
            // 用 Link 而非 NavLink：理由見 ServiceRail 的 RailItem
            component={Link}
            to={section.to}
            aria-label={section.label}
            aria-current={active ? "page" : undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.25,
              color: active ? "text.primary" : "text.secondary",
              "&:active": { bgcolor: "action.hover" },
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
            <Typography
              variant="caption"
              sx={{ fontSize: 11, fontWeight: active ? 600 : 400, lineHeight: 1 }}
            >
              {section.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
